import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import SuccessHeader from './components/SuccessHeader';
import OrderConfirmationCard from './components/OrderConfirmationCard';
import ReceiptSection from './components/ReceiptSection';
import TrackingInfoSection from './components/TrackingInfoSection';
import ActionButtons from './components/ActionButtons';

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cartCount, setCartCount] = useState(0);
  const [user, setUser] = useState(null);
  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    // Load user data
    const mockUser = localStorage.getItem('currentUser');
    if (mockUser) {
      setUser(JSON.parse(mockUser));
    }

    const mockCartCount = localStorage.getItem('cartCount');
    if (mockCartCount) {
      setCartCount(parseInt(mockCartCount, 10));
    }

    // Get order data from navigation state or generate mock data
    if (location?.state?.orderData) {
      setOrderData(location?.state?.orderData);
    } else {
      // Generate mock order data for demonstration
      const mockOrderData = {
        orderNumber: `ORD-${Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase()}`,
        orderDate: new Date()?.toISOString(),
        totalAmount: 352.97,
        paymentMethod: 'Visa ending in 4242',
        stripeTransactionId: `pi_${Math.random()?.toString(36)?.substr(2, 24)}`,
        transactionTimestamp: new Date()?.toISOString(),
        items: [
          {
            id: 1,
            name: 'Margherita Pizza',
            quantity: 2,
            price: 14.99,
            image: 'https://images.unsplash.com/photo-1574071318508-1cdbab80d002',
            imageAlt: 'Fresh Margherita pizza with basil and mozzarella cheese',
            customizations: ['Extra Cheese', 'Thin Crust']
          },
          {
            id: 2,
            name: 'Caesar Salad',
            quantity: 1,
            price: 9.99,
            image: 'https://images.unsplash.com/photo-1546793665-c74683f339c1',
            imageAlt: 'Classic Caesar salad with romaine lettuce and parmesan',
            customizations: ['No Croutons']
          },
          {
            id: 3,
            name: 'Tiramisu',
            quantity: 2,
            price: 7.99,
            image: 'https://images.unsplash.com/photo-1571877227200-a0d98ea607e9',
            imageAlt: 'Traditional Italian tiramisu dessert with cocoa powder',
            customizations: []
          }
        ],
        subtotal: 55.95,
        deliveryFee: 3.99,
        tax: 4.48,
        estimatedDelivery: new Date(Date.now() + 45 * 60000)?.toISOString(), // 45 minutes from now
        deliveryAddress: {
          street: '123 Main Street',
          city: 'New York',
          state: 'NY',
          zip: '10001'
        },
        restaurant: {
          name: 'Pesto Italian Kitchen',
          phone: '(555) 123-4567',
          address: '456 Restaurant Ave, New York, NY 10002'
        },
        trackingNumber: `TRK-${Math.random()?.toString(36)?.substr(2, 10)?.toUpperCase()}`,
        driverInfo: {
          name: 'John Delivery',
          phone: '(555) 987-6543',
          vehicle: 'Honda Civic - ABC 1234'
        }
      };
      setOrderData(mockOrderData);
    }

    // Clear cart after successful checkout
    localStorage.setItem('cartCount', '0');
    setCartCount(0);
  }, [location]);

  const handleCartClick = () => {
    navigate('/shopping-cart');
  };

  const handleAccountClick = (action) => {
    switch (action) {
      case 'login': navigate('/login');
        break;
      case 'register': navigate('/register');
        break;
      case 'account': navigate('/user-account');
        break;
      case 'logout': localStorage.removeItem('currentUser');
        setUser(null);
        break;
      default:
        break;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    setUser(null);
  };

  const handleSearch = (searchTerm) => {
    // Mock search functionality
    console.log('Search term:', searchTerm);
  };

  const handleDownloadReceipt = () => {
    // Mock PDF download
    alert(`Receipt for order ${orderData?.orderNumber} will be downloaded`);
  };

  const handleEmailReceipt = () => {
    // Mock email sending
    alert(`Receipt sent to ${user?.email || 'your email'}`);
  };

  const handleTrackOrder = () => {
    navigate('/order-tracking', { 
      state: { 
        trackingNumber: orderData?.trackingNumber,
        orderData: orderData 
      } 
    });
  };

  const handleContinueShopping = () => {
    navigate('/menu-catalog');
  };

  if (!orderData) {
    return (
      <div className="min-h-screen bg-background">
        <Header
          cartCount={cartCount}
          user={user}
          onCartClick={handleCartClick}
          onAccountClick={handleAccountClick}
          onLogout={handleLogout}
          onSearch={handleSearch}
        />
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-muted-foreground">Loading order details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header
        cartCount={cartCount}
        user={user}
        onCartClick={handleCartClick}
        onAccountClick={handleAccountClick}
        onLogout={handleLogout}
        onSearch={handleSearch}
      />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-6"
        >
          {/* Success Header */}
          <SuccessHeader orderNumber={orderData?.orderNumber} />

          {/* Order Confirmation Card */}
          <OrderConfirmationCard
            orderNumber={orderData?.orderNumber}
            totalAmount={orderData?.totalAmount}
            paymentMethod={orderData?.paymentMethod}
            stripeTransactionId={orderData?.stripeTransactionId}
            transactionTimestamp={orderData?.transactionTimestamp}
            orderDate={orderData?.orderDate}
          />

          {/* Receipt Section */}
          <ReceiptSection
            items={orderData?.items}
            subtotal={orderData?.subtotal}
            deliveryFee={orderData?.deliveryFee}
            tax={orderData?.tax}
            total={orderData?.totalAmount}
          />

          {/* Tracking Info Section */}
          <TrackingInfoSection
            trackingNumber={orderData?.trackingNumber}
            estimatedDelivery={orderData?.estimatedDelivery}
            deliveryAddress={orderData?.deliveryAddress}
            driverInfo={orderData?.driverInfo}
            restaurant={orderData?.restaurant}
          />

          {/* Action Buttons */}
          <ActionButtons
            onDownloadReceipt={handleDownloadReceipt}
            onEmailReceipt={handleEmailReceipt}
            onTrackOrder={handleTrackOrder}
            onContinueShopping={handleContinueShopping}
          />
        </motion.div>
      </main>
    </div>
  );
};

export default CheckoutSuccess;